import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Plane, Building2, Landmark, ShieldCheck, Handshake, TrendingUp, Network,
    Calculator, Users, FileCheck, LineChart, Target, DoorOpen, ArrowRight,
    CheckCircle2, Sparkles, Send,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { getPageStrings } from "@/lib/pageStrings";
import LanguagePicker from "@/components/LanguagePicker";

const ICONS = {
    Plane, Building2, Landmark, ShieldCheck, Handshake, TrendingUp, Network,
    Calculator, Users, FileCheck, LineChart, Target, DoorOpen,
};

export default function Services() {
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [requestService, setRequestService] = useState(null);
    const { lang } = useLang();
    const t = getPageStrings("services", lang);

    useEffect(() => {
        api.get("/services")
            .then(({ data }) => setCatalog(data.items || []))
            .catch(() => toast.error("Could not load services"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="services-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-20 lg:py-28">
                <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-coral/10 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-turquoise/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                        <Sparkles className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            {t.heroKicker}
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5 max-w-4xl">
                        {t.heroTitlePre}<span className="italic text-turquoise">{t.heroTitleAccent}</span>
                    </h1>
                    <p className="font-app text-base sm:text-lg text-white/85 leading-relaxed max-w-3xl mb-6">
                        {t.heroSubtitle}
                    </p>
                    <LanguagePicker variant="dark" testId="services-lang-select" />
                    <div className="grid sm:grid-cols-3 gap-4 mt-10">
                        {[
                            { stat: "13", label: t.statsCategories },
                            { stat: "8+", label: t.statsAgencies },
                            { stat: "1", label: t.statsContact },
                        ].map((m) => (
                            <div key={m.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                                <div className="font-display text-3xl font-bold text-turquoise tabular-nums">{m.stat}</div>
                                <div className="text-xs uppercase tracking-wider text-white/70 font-app mt-1">{m.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES GRID */}
            <section className="py-16 lg:py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-10">
                        <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground">
                            {t.bodyTitle}
                        </h2>
                        <p className="font-app text-base text-muted-foreground max-w-2xl">
                            {t.bodyIntro}
                        </p>
                    </div>

                    {loading ? (
                        <div className="text-center font-app text-muted-foreground py-20">{t.loadingLabel}</div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {catalog.map((svc, i) => {
                                const Icon = ICONS[svc.icon] || Handshake;
                                return (
                                    <Card
                                        key={svc.slug}
                                        data-testid={`service-card-${svc.slug}`}
                                        className="p-6 hover:border-coral/50 transition-all hover:shadow-lg cursor-pointer group bg-card"
                                        onClick={() => setRequestService(svc)}
                                    >
                                        <div className="flex items-start gap-3 mb-4">
                                            <div className="h-11 w-11 rounded-lg bg-coral/10 grid place-items-center group-hover:bg-coral/20 transition shrink-0">
                                                <Icon className="h-5 w-5 text-coral" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app font-bold mb-1">
                                                    {String(i + 1).padStart(2, "0")}
                                                </div>
                                                <h3 className="font-display text-lg font-bold leading-tight text-foreground">
                                                    {svc.title}
                                                </h3>
                                            </div>
                                        </div>
                                        <p className="font-app text-sm text-coral font-semibold mb-2 leading-snug">
                                            {svc.lead_in}
                                        </p>
                                        <p className="font-app text-sm text-muted-foreground leading-relaxed mb-4">
                                            {svc.blurb}
                                        </p>
                                        <ul className="space-y-1.5 mb-5">
                                            {svc.services.slice(0, 3).map((s, j) => (
                                                <li key={j} className="flex items-start gap-2 text-xs font-app text-foreground/80">
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-fiji-green mt-0.5 shrink-0" />
                                                    <span>{s}</span>
                                                </li>
                                            ))}
                                            {svc.services.length > 3 && (
                                                <li className="text-xs font-app text-muted-foreground italic pl-5.5">
                                                    + {svc.services.length - 3} {t.moreLabel}
                                                </li>
                                            )}
                                        </ul>
                                        <Button
                                            size="sm"
                                            data-testid={`service-request-btn-${svc.slug}`}
                                            className="w-full bg-ocean-green hover:bg-ocean text-white font-app group-hover:bg-coral group-hover:hover:bg-coral-deep transition"
                                        >
                                            {t.requestCta}
                                            <ArrowRight className="h-3.5 w-3.5 ml-1.5 group-hover:translate-x-0.5 transition" />
                                        </Button>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="py-14 bg-coral/5 dark:bg-coral/10 border-y border-coral/20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-2xl lg:text-3xl font-bold mb-3 text-foreground">
                        {t.bottomTitle}
                    </h2>
                    <p className="font-app text-muted-foreground mb-5 max-w-2xl mx-auto">
                        {t.bottomBody}
                    </p>
                    <Button asChild size="lg" data-testid="services-cta-strategy" className="bg-coral hover:bg-coral-deep text-white">
                        <Link to="/invest">{t.bottomCta} <ArrowRight className="h-4 w-4 ml-2" /></Link>
                    </Button>
                </div>
            </section>

            {requestService && (
                <ServiceRequestDialog
                    service={requestService}
                    onClose={() => setRequestService(null)}
                />
            )}
        </div>
    );
}

function ServiceRequestDialog({ service, onClose }) {
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "", country: "",
        message: `I'd like assistance with ${service.title.toLowerCase()}.`,
        budget_band: "", timeline: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.message) {
            toast.error("Name, email, and message are required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/services/request", { ...form, service_slug: service.slug });
            setDone(true);
            toast.success("Request sent — we'll reply within one business day.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="service-request-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">Request: {service.title}</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        {service.lead_in}
                    </DialogDescription>
                </DialogHeader>

                {done ? (
                    <div className="py-8 text-center" data-testid="service-request-done">
                        <CheckCircle2 className="h-12 w-12 text-fiji-green mx-auto mb-3" />
                        <div className="font-display text-xl font-bold text-foreground mb-2">Request received</div>
                        <p className="font-app text-sm text-muted-foreground">
                            Our team will reply to <strong className="text-foreground">{form.email}</strong> within one business day.
                        </p>
                        <Button onClick={onClose} className="mt-5 bg-ocean-green text-white">Close</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3" data-testid="service-request-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Full name *</Label>
                                <Input data-testid="service-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Email *</Label>
                                <Input data-testid="service-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Phone (E.164)</Label>
                                <Input data-testid="service-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1..." className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Country</Label>
                                <Input data-testid="service-country" value={form.country} onChange={(e) => update("country", e.target.value)} placeholder="USA / Australia / NZ…" className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Investment band</Label>
                                <Select value={form.budget_band} onValueChange={(v) => update("budget_band", v)}>
                                    <SelectTrigger data-testid="service-budget" className="bg-card text-foreground"><SelectValue placeholder="Select…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="under-250k">Under USD 250k</SelectItem>
                                        <SelectItem value="250k-1m">USD 250k – 1m</SelectItem>
                                        <SelectItem value="1m-5m">USD 1m – 5m</SelectItem>
                                        <SelectItem value="5m-plus">USD 5m+</SelectItem>
                                        <SelectItem value="exploring">Just exploring</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Timeline</Label>
                                <Select value={form.timeline} onValueChange={(v) => update("timeline", v)}>
                                    <SelectTrigger data-testid="service-timeline" className="bg-card text-foreground"><SelectValue placeholder="Select…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="now">Ready now</SelectItem>
                                        <SelectItem value="3-months">Within 3 months</SelectItem>
                                        <SelectItem value="6-months">Within 6 months</SelectItem>
                                        <SelectItem value="exploring">Exploring</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Tell us about your goal *</Label>
                            <Textarea data-testid="service-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} required className="bg-card text-foreground resize-none" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                            <Button type="submit" disabled={submitting} data-testid="service-submit-btn" className="bg-coral hover:bg-coral-deep text-white">
                                <Send className="h-4 w-4 mr-2" />
                                {submitting ? "Sending…" : "Send request"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
